// Combo Report: Natal + Solar combined content
// Person: Алексей Петров (15.08.1992, 14:30, Moscow)
// Sun: Leo, Moon: Scorpio, Asc: Sagittarius
// Solar year: 2026

// ═══════════════════════════════════════════
// PART 1 — NATAL CHART
// ═══════════════════════════════════════════

export const COMBO_NATAL_INTRO = {
  title: 'Введение',
  whatIsNatalChart: `Натальная карта — это астрономическая карта неба, зафиксированная на момент вашего рождения. Она представляет собой точную проекцию положений Солнца, Луны и планет Солнечной системы относительно знаков зодиака и домов гороскопа. По сути, натальная карта — это ваш космический паспорт, описывающий набор энергий, талантов, склонностей и потенциальных вызовов, с которыми вы пришли в этот мир.`,
  whatItShows: `Каждый элемент карты несёт определённый смысл. Планеты символизируют различные функции психики: воля, эмоции, мышление, любовь, действие. Знаки зодиака показывают стиль, в котором эти функции проявляются. Дома гороскопа указывают на конкретные сферы жизни, где энергии будут наиболее активны. Аспекты описывают внутренние диалоги и конфликты между различными частями вашей личности.`,
  whatIsAnalyzed: `В первой части этого отчёта мы подробно разберём вашу натальную карту: характер, эмоциональную природу, стиль мышления, подход к любви и карьере, внутренние конфликты и таланты. Во второй части мы наложим на натальную карту солярный прогноз на 2026 год и покажем, как энергии года взаимодействуют с вашим врождённым потенциалом.`,
}

export const COMBO_PERSONALITY = {
  title: 'Общая характеристика личности',
  overview: `Алексей, ваша натальная карта рисует портрет яркой, многогранной и невероятно глубокой личности. Сочетание Солнца во Льве, Луны в Скорпионе и Асцендента в Стрельце создаёт уникальный характер, в котором внешняя яркость и оптимизм сочетаются с внутренней глубиной и интенсивностью.`,
  traits: [
    {
      trait: 'Харизма и лидерство',
      description:
        'Солнце во Льве наделяет вас природной способностью привлекать внимание и вести за собой. Вы не просто хотите быть замеченным — вы хотите вдохновлять.',
    },
    {
      trait: 'Эмоциональная глубина',
      description:
        'Луна в Скорпионе создаёт мощный внутренний мир, скрытый за солнечной улыбкой Льва. Вы чувствуете всё на порядок интенсивнее, чем большинство людей.',
    },
    {
      trait: 'Философский размах',
      description:
        'Асцендент в Стрельце добавляет стремление к расширению горизонтов, поиску смысла и большим идеям. Вы не довольствуетесь малым.',
    },
    {
      trait: 'Стратегическое мышление',
      description:
        'Доминанта фиксированного креста (5 планет в фиксированных знаках) даёт вам упорство, стойкость и способность доводить дела до конца.',
    },
  ],
  motivations: `Ваши внутренние мотивации формируются на пересечении трёх мощных энергий. Лев стремится к признанию и самовыражению. Скорпион ищет глубину, истину и трансформацию. Стрелец жаждет свободы, приключений и мудрости. Когда эти три силы работают в согласии, вы становитесь невероятно эффективным — ярким лидером с глубоким пониманием людей и масштабным видением.`,
  values: `Ваши ключевые ценности: подлинность (вы не терпите фальши), верность (предательство — ваш главный триггер), свобода мысли (вы не принимаете догмы на веру) и щедрость (вы готовы отдать многое тем, кого любите).`,
}

export const COMBO_SUN = {
  title: 'Солнце во Льве — Ядро личности',
  subtitle: 'Обитель Солнца • 9-й дом • Огненная стихия',
  description: `Солнце во Льве в 9-м доме — это положение силы и масштаба. Ваше ядро личности ориентировано на самовыражение через знания, философию и наставничество. Вы не просто хотите блистать — вы хотите нести свет и знания другим людям.`,
  lifeGoals: `Ваша жизненная цель — стать подлинным лидером и наставником. Не тем, кто командует, а тем, кто вдохновляет своим примером. 9-й дом добавляет к львиной яркости стремление к высшему образованию, путешествиям и философскому осмыслению жизни. Вы находите себя через преподавание, публичные выступления и расширение горизонтов.`,
  personality: `В повседневной жизни вы проявляетесь как человек щедрый, уверенный и творческий. Вы любите быть в центре внимания, но не ради тщеславия — вам важно чувствовать, что вы вносите вклад. Рутинная работа без признания буквально лишает вас жизненных сил.`,
  potential: `Ваш внутренний потенциал огромен. Соединение Солнца и Меркурия во Льве создаёт мощный интеллектуально-творческий тандем. Вы — прирождённый оратор, писатель или преподаватель, способный зажечь аудиторию своими идеями.`,
  strengths: [
    'Природная харизма и лидерство',
    'Щедрость и великодушие',
    'Творческий потенциал',
    'Способность вдохновлять',
  ],
  shadows: [
    'Потребность в восхищении',
    'Склонность к драматизации',
    'Уязвимость к лести',
    'Трудность с принятием критики',
  ],
}

export const COMBO_MOON = {
  title: 'Луна в Скорпионе — Эмоциональная природа',
  subtitle: 'Падение Луны • 12-й дом • Водная стихия',
  description: `Луна в Скорпионе в 12-м доме создаёт один из самых интенсивных эмоциональных паттернов в астрологии. Ваши чувства никогда не бывают поверхностными — вы либо любите до безумия, либо отвергаете полностью. Середины для вас не существует.`,
  emotionalReactions: `Ваши эмоциональные реакции мгновенны и мощны, но вы научились их скрывать. Внешне вы можете казаться спокойным и даже отстранённым, но внутри бушует вулкан. Вы сканируете окружающих на предмет угрозы, считываете скрытые мотивы и запоминаете каждую обиду.`,
  innerNeeds: `Ваши глубинные потребности: абсолютная честность в отношениях, эмоциональная интенсивность (спокойная жизнь кажется вам пресной), контроль над ситуацией и постоянная трансформация. Вы не можете стоять на месте — вам нужно углубляться, исследовать и разрушать старое, чтобы строить новое.`,
  feelingStyle: `Вы переживаете чувства на уровне, недоступном большинству людей. 12-й дом добавляет к скорпионьей интенсивности мистическое измерение: вы можете видеть пророческие сны, чувствовать энергетику мест и людей, обладать целительскими способностями. Но здесь же скрыты ваши глубочайшие страхи.`,
}

export const COMBO_ASCENDANT = {
  title: 'Асцендент в Стрельце — Внешнее проявление',
  subtitle: 'Управитель: Юпитер • Огненная стихия',
  description: `Асцендент в Стрельце определяет вашу социальную маску и первое впечатление, которое вы производите. Вы кажетесь открытым, оптимистичным и немного авантюрным человеком — тем, кто знает, куда идёт, и приглашает других присоединиться.`,
  externalPresentation: `Во внешнем мире вы проявляетесь как человек энергичный, прямолинейный и философски настроенный. Ваша речь часто содержит обобщения и масштабные идеи. Вы не любите мелочиться и предпочитаете думать о «большой картине».`,
  firstImpression: `Первое впечатление, которое вы производите — уверенный, открытый, немного громкий человек с широкой улыбкой и крепким рукопожатием. Люди тянутся к вашему оптимизму и энергии. Вы кажетесь человеком, с которым не будет скучно.`,
  behaviorStyle: `Ваш стиль поведения — прямой, честный, иногда бестактный. Вы говорите то, что думаете, и не понимаете, зачем ходить вокруг да около. Это привлекает одних людей и отталкивает других. Вы предпочитаете действовать, а не планировать, и часто принимаете решения на ходу.`,
}

export const COMBO_PLANETS_IN_SIGNS = [
  {
    planet: 'Меркурий',
    symbol: '☿',
    sign: 'Лев',
    house: 9,
    description:
      'Мышление яркое, масштабное и творческое. Вы учитесь лучше всего, когда материал подан с примерами и историями. Ваш идеальный способ обучения — преподавание. Склонность к обобщениям и категоричности в суждениях.',
    strengths: [
      'Ораторский талант',
      'Убедительная речь',
      'Творческое мышление',
    ],
  },
  {
    planet: 'Венера',
    symbol: '♀',
    sign: 'Рак',
    house: 8,
    description:
      'Язык любви — забота, эмоциональная поддержка и создание уюта. Вы выражаете любовь через приготовление еды, создание безопасного пространства и нежные прикосновения. Финансовое поведение осторожное, деньги = безопасность.',
    strengths: [
      'Глубокая привязанность',
      'Забота о близких',
      'Эстетический вкус',
    ],
  },
  {
    planet: 'Марс',
    symbol: '♂',
    sign: 'Близнецы',
    house: 7,
    description:
      'Энергия действия направлена через интеллект и коммуникацию. В конфликте вы используете слова как оружие — сарказм, ирония, безупречная логика. Склонность к многозадачности и рассеиванию энергии.',
    strengths: ['Интеллектуальная стратегия', 'Быстрая реакция', 'Гибкость'],
  },
  {
    planet: 'Юпитер',
    symbol: '♃',
    sign: 'Дева',
    house: 10,
    description:
      'Удача приходит через компетентность, а не через везение. Вы расширяете горизонты через совершенствование навыков. Карьерный рост через экспертность и служение. Склонность к перфекционизму.',
    strengths: [
      'Профессиональный рост',
      'Внимание к деталям',
      'Практичная мудрость',
    ],
  },
  {
    planet: 'Сатурн',
    symbol: '♄',
    sign: 'Водолей',
    house: 3,
    description:
      'Серьёзный подход к общению и обучению. В юности могли быть трудности с выражением мыслей. С возрастом становитесь всё более авторитетным интеллектуальным лидером. Мыслите нестандартно, но выражаете идеи структурированно.',
    strengths: [
      'Структурное мышление',
      'Интеллектуальная зрелость',
      'Ответственность за слова',
    ],
  },
  {
    planet: 'Уран',
    symbol: '♅',
    sign: 'Козерог',
    house: 2,
    description:
      'Нестандартный подход к деньгам и ценностям. Заработок через технологии, интернет, инновации. Финансы могут быть нестабильными — периоды изобилия чередуются с неожиданными потерями.',
    strengths: [
      'Инновационный подход к финансам',
      'Технологическая грамотность',
      'Независимость',
    ],
  },
  {
    planet: 'Нептун',
    symbol: '♆',
    sign: 'Козерог',
    house: 2,
    description:
      'Размытые финансовые границы: склонность к щедрости без учёта последствий. Доход возможен через творчество, духовные практики или благотворительность. Важно отслеживать расходы.',
    strengths: [
      'Творческий подход к заработку',
      'Духовные ценности',
      'Интуиция в финансах',
    ],
  },
  {
    planet: 'Плутон',
    symbol: '♇',
    sign: 'Скорпион',
    house: 12,
    description:
      'Огромная скрытая психическая сила. Способность к глубокой трансформации себя и окружающих. Прирождённый психолог или целитель. Склонность к навязчивым мыслям и страхам, связанным с потерей контроля.',
    strengths: [
      'Трансформационная сила',
      'Психологическая проницательность',
      'Способность к возрождению',
    ],
  },
]

export const COMBO_HOUSES_SUMMARY = [
  {
    house: 1,
    sign: 'Стрелец',
    theme: 'Личность',
    text: 'Оптимистичная, открытая подача. Впечатление человека, который знает, куда идёт.',
  },
  {
    house: 2,
    sign: 'Козерог',
    theme: 'Финансы',
    text: 'Серьёзное отношение к деньгам с элементами нестабильности (Уран, Нептун).',
  },
  {
    house: 3,
    sign: 'Водолей',
    theme: 'Общение',
    text: 'Нестандартное мышление, структурированное выражение (Сатурн).',
  },
  {
    house: 4,
    sign: 'Рыбы',
    theme: 'Дом и семья',
    text: 'Чувствительная домашняя обстановка. Стремление создать дом-убежище.',
  },
  {
    house: 5,
    sign: 'Овен',
    theme: 'Творчество',
    text: 'Страстный, импульсивный подход к творчеству и романтике.',
  },
  {
    house: 6,
    sign: 'Телец',
    theme: 'Работа',
    text: 'Основательный рабочий стиль. Внимание к горлу и обмену веществ.',
  },
  {
    house: 7,
    sign: 'Близнецы',
    theme: 'Партнёрство',
    text: 'Потребность в интеллектуально стимулирующем партнёре (Марс).',
  },
  {
    house: 8,
    sign: 'Рак',
    theme: 'Трансформация',
    text: 'Трансформация через любовь и эмоциональные кризисы (Венера).',
  },
  {
    house: 9,
    sign: 'Лев',
    theme: 'Философия',
    text: 'Главный дом реализации. Наставничество и преподавание (Солнце, Меркурий).',
  },
  {
    house: 10,
    sign: 'Дева',
    theme: 'Карьера',
    text: 'Успех через экспертность и внимание к деталям (Юпитер).',
  },
  {
    house: 11,
    sign: 'Весы',
    theme: 'Друзья',
    text: 'Друзья из мира искусства и дипломатии. Мечты о справедливом мире.',
  },
  {
    house: 12,
    sign: 'Скорпион',
    theme: 'Подсознание',
    text: 'Мощная скрытая психическая сила. Пророческие сны (Луна, Плутон).',
  },
]

export const COMBO_NATAL_ASPECTS = [
  {
    aspect: '☉ ☌ ☿',
    title: 'Солнце соединение Меркурий',
    nature: 'intense' as const,
    text: 'Мышление неотделимо от эго. Блестящий оратор и писатель, но субъективность в оценках.',
  },
  {
    aspect: '☽ ☌ ♇',
    title: 'Луна соединение Плутон',
    nature: 'intense' as const,
    text: 'Невероятная эмоциональная глубина и психическая сила. Интуиция на уровне рентгена, но навязчивые эмоции.',
  },
  {
    aspect: '☉ △ ASC',
    title: 'Солнце тригон Асцендент',
    nature: 'harmonious' as const,
    text: 'Естественная цельность: внутреннее содержание легко проявляется вовне. Харизма и искренность.',
  },
  {
    aspect: '☽ △ ♀',
    title: 'Луна тригон Венера',
    nature: 'harmonious' as const,
    text: 'Эмоциональная гармония. Способность к настоящей, глубокой любви и созданию тепла.',
  },
  {
    aspect: '♀ ⚹ ♃',
    title: 'Венера секстиль Юпитер',
    nature: 'harmonious' as const,
    text: 'Природное обаяние, хороший вкус, умение привлекать благоприятные обстоятельства.',
  },
  {
    aspect: '♂ □ ♄',
    title: 'Марс квадрат Сатурн',
    nature: 'tense' as const,
    text: 'Внутренний конфликт между действием и самоцензурой. Подавленная агрессия, но огромная выносливость.',
  },
  {
    aspect: '♂ □ ♃',
    title: 'Марс квадрат Юпитер',
    nature: 'tense' as const,
    text: 'Склонность брать слишком много. Огромная энергия, но нужно учиться чувству меры.',
  },
]

export const COMBO_NATAL_LIFE_AREAS = {
  career: {
    title: 'Карьера и реализация',
    icon: 'briefcase',
    text: `С Юпитером в 10-м доме в Деве ваш социальный успех приходит через экспертность и служение. Вы не из тех, кого повышают за связи — вас повышают за реальные заслуги. Идеальные сферы: образование, IT, медицина, аналитика, консалтинг. Солнце в 9-м доме добавляет международное измерение — работа с иностранцами, преподавание, издательская деятельность.`,
  },
  relationships: {
    title: 'Отношения и любовь',
    icon: 'heart',
    text: `Марс в 7-м доме в Близнецах делает сферу партнёрства динамичной и конфликтной. Вам нужен партнёр, который интеллектуально стимулирует вас. Венера в Раке ищет нежности и домашнего уюта. Главный урок — научиться доверять и не контролировать (Луна в Скорпионе).`,
  },
  finances: {
    title: 'Финансы',
    icon: 'trending-up',
    text: `2-й дом в Козероге с Ураном и Нептуном создаёт сложную финансовую картину. Козерог даёт серьёзное отношение к деньгам, Уран вносит нестабильность, Нептун размывает границы. Путь к стабильности — через инновации с практическим применением и несколько источников дохода.`,
  },
  development: {
    title: 'Личностное развитие',
    icon: 'compass',
    text: `Ваш путь развития лежит через интеграцию противоположностей: свет Льва и тень Скорпиона, контроль и доверие, независимость и привязанность. Северный узел в Стрельце указывает на движение от детализации к масштабному видению, от логики к интуиции.`,
  },
  social: {
    title: 'Социальная жизнь',
    icon: 'users',
    text: `11-й дом в Весах привлекает в вашу жизнь людей искусства, дипломатии и гармонии. Вы цените красивые, сбалансированные отношения в дружбе. Сатурн в 3-м доме может создавать ощущение «белой вороны», но с возрастом вы становитесь авторитетным интеллектуальным лидером.`,
  },
}

// ═══════════════════════════════════════════
// PART 2 — SOLAR FORECAST
// ═══════════════════════════════════════════

export const COMBO_SOLAR_INTRO = {
  title: 'Введение в соляр',
  whatIsSolar: `Соляр (солярное возвращение) — это астрологическая карта, построенная на момент точного возвращения Солнца в ту же позицию, что и при рождении. Это происходит ежегодно вблизи дня рождения и определяет основные темы, события и энергии предстоящего года.`,
  howItAffects: `Солярная карта накладывается на натальную, активируя определённые дома и планеты. Если натальная карта — это сценарий всей жизни, то соляр — это сценарий конкретного года. Он показывает, какие врождённые потенциалы будут востребованы, какие сферы жизни выйдут на первый план и с какими вызовами предстоит столкнуться.`,
}

export const COMBO_YEAR_ENERGY = {
  title: 'Общая энергия 2026 года',
  mainThemes: [
    'Карьерный прорыв и рост социального статуса',
    'Интенсивное обучение и нетворкинг',
    'Расширение жилищных условий',
    'Трансформация мировоззрения',
  ],
  changes: `Основные изменения коснутся вашего социального статуса и профессиональной реализации. Солнце и Сатурн в 10-м доме соляра создают мощный карьерный фокус. Возможна смена должности, переход на новый уровень ответственности или запуск важного публичного проекта.`,
  tasks: `Ключевые задачи года: взять на себя серьёзную профессиональную ответственность, научиться балансировать карьеру и семью (оппозиция Юпитер-Сатурн), управлять информационным потоком (Асцендент в Близнецах) и не допустить эмоционального выгорания (Луна в 6-м доме).`,
  atmosphere: `Психологически год будет ощущаться как очень динамичный и продуктивный, но местами суетливый. Марс в 1-м доме даёт много личной энергии, но Луна в Скорпионе в 6-м доме предупреждает о риске психосоматики при переутомлении.`,
}

export const COMBO_SOLAR_ASCENDANT = {
  title: 'Асцендент соляра в Близнецах',
  subtitle: '♊ Близнецы',
  manifestation: `В 2026 году вы будете проявляться как человек лёгкий на подъём, любознательный и многозадачный. Ваша речь станет более быстрой, а ум — более цепким. Окружающие увидят в вас интересного собеседника и генератора идей.`,
  behaviorStyle: `Ваш стиль поведения в этом году — адаптивный и коммуникативный. Вы будете много общаться, перемещаться и обрабатывать информацию. Не цепляйтесь за старые планы, если ситуация меняется. Гибкость — ваш главный инструмент.`,
  perception: `Люди будут воспринимать вас как человека молодого душой, энергичного и компетентного. К вам будут тянуться за советом, свежими идеями и просто ради интересной беседы.`,
}

export const COMBO_SOLAR_SUN = {
  title: 'Солнце в 10 доме соляра',
  subtitle: 'Лев • 10-й дом',
  description: `Положение Солнца в 10-м доме — классическое указание на год карьерных достижений. Ваше эго, ваша потребность в признании и самовыражении будут направлены в профессиональную сферу. Вы захотите, чтобы ваши заслуги были признаны официально.`,
  keySphere:
    'Карьера, репутация, отношения с властью, достижение долгосрочных целей.',
  events: [
    'Повышение в должности или получение важного проекта',
    'Официальное признание заслуг',
    'Смена социального статуса',
    'Успешное взаимодействие с руководством',
  ],
}

export const COMBO_SOLAR_MOON = {
  title: 'Луна в 6 доме соляра',
  subtitle: 'Скорпион • 6-й дом',
  description: `Луна в 6-м доме в Скорпионе указывает на интенсивную эмоциональную вовлечённость в рабочие процессы. Вы будете глубоко переживать рабочие ситуации, что может привести как к невероятной продуктивности, так и к эмоциональному выгоранию.`,
  emotionalAtmosphere: `Эмоциональный фон года напряжённый из-за высокой ответственности. Возможна склонность к психосоматике при переутомлении. Важно найти экологичные способы сброса напряжения и не копить обиды на коллег.`,
}

export const COMBO_SOLAR_PLANETS = [
  {
    planet: 'Меркурий',
    symbol: '☿',
    sign: 'Лев',
    house: 3,
    text: 'Год интенсивного обучения, коротких поездок и активного нетворкинга. Ум работает ярко и творчески. Отличное время для публичных выступлений и написания статей.',
  },
  {
    planet: 'Венера',
    symbol: '♀',
    sign: 'Рак',
    house: 2,
    text: 'Финансы связаны с эмоциями и семьёй. Доходы через недвижимость или семейный бизнес. Траты часто эмоциональные — на обустройство дома.',
  },
  {
    planet: 'Марс',
    symbol: '♂',
    sign: 'Близнецы',
    house: 1,
    text: 'Огромный заряд личной энергии. Инициативность и смелость. Склонность к спорам и поспешным действиям.',
  },
  {
    planet: 'Юпитер',
    symbol: '♃',
    sign: 'Дева',
    house: 4,
    text: 'Расширение в сфере дома и семьи. Благоприятный год для решения жилищных вопросов, ремонта, переезда.',
  },
  {
    planet: 'Сатурн',
    symbol: '♄',
    sign: 'Рыбы',
    house: 10,
    text: 'Серьёзная ответственность в карьере. Требует дисциплины и профессионализма. Закладка фундамента успеха.',
  },
  {
    planet: 'Уран',
    symbol: '♅',
    sign: 'Близнецы',
    house: 12,
    text: 'Внезапные озарения и освобождение от скрытых страхов. Интерес к эзотерике и психологии.',
  },
  {
    planet: 'Нептун',
    symbol: '♆',
    sign: 'Овен',
    house: 11,
    text: 'Идеализация друзей. Творческие и духовные сообщества. Важно не стать жертвой обмана.',
  },
  {
    planet: 'Плутон',
    symbol: '♇',
    sign: 'Водолей',
    house: 9,
    text: 'Глубокая трансформация мировоззрения. Интенсивные события с заграницей или образованием.',
  },
]

export const COMBO_SOLAR_ASPECTS = [
  {
    aspect: '☉ △ ASC',
    title: 'Солнце тригон Асцендент',
    nature: 'harmonious' as const,
    text: 'Жизненная сила, уверенность и способность легко реализовывать намерения. Гармония внутреннего и внешнего.',
  },
  {
    aspect: '☽ □ ♂',
    title: 'Луна квадрат Марс',
    nature: 'tense' as const,
    text: 'Эмоциональная импульсивность. Конфликты на работе из-за резких слов. Направляйте энергию в спорт.',
  },
  {
    aspect: '♃ ☍ ♄',
    title: 'Юпитер оппозиция Сатурн',
    nature: 'tense' as const,
    text: 'Конфликт между расширением (дом, семья) и профессиональным долгом (карьера). Баланс — ключ.',
  },
  {
    aspect: '☿ ⚹ ♅',
    title: 'Меркурий секстиль Уран',
    nature: 'harmonious' as const,
    text: 'Гениальные идеи и нестандартное мышление. Отличное время для изучения технологий и инноваций.',
  },
]

export const COMBO_KEY_PERIODS = {
  favorable: [
    {
      period: 'Март — Май',
      description:
        'Максимум карьерных возможностей. Юпитер тригон натальное Солнце. Идеальное время для запуска проектов и переговоров.',
    },
    {
      period: 'Август',
      description:
        'Новый солярный цикл. Обновление энергии. Хорошее время для постановки целей на следующий год.',
    },
    {
      period: 'Декабрь',
      description:
        'Венера секстиль Юпитер. Гармония в финансах и отношениях. Удачное завершение года.',
    },
  ],
  tense: [
    {
      period: 'Июнь',
      description:
        'Сатурн квадрат натальная Луна. Эмоциональное напряжение на работе. Риск выгорания. Берегите себя.',
    },
    {
      period: 'Октябрь',
      description:
        'Затмение в 5-м доме. Неожиданные события в сфере творчества или романтики. Не принимайте импульсивных решений.',
    },
  ],
  action: [
    {
      period: 'Март',
      description:
        'Новолуние в 10-м доме. Запуск нового карьерного цикла. Действуйте решительно.',
    },
    {
      period: 'Апрель — Май',
      description:
        'Юпитер тригон Солнце. Удача на вашей стороне. Расширяйте влияние.',
    },
    {
      period: 'Сентябрь',
      description:
        'Марс активирует 1-й дом. Время для личных инициатив и самопродвижения.',
    },
  ],
}

export const COMBO_SOLAR_NATAL_INTERACTION = {
  title: 'Влияние соляра на натальную карту',
  description: `Когда солярная карта накладывается на натальную, определённые врождённые темы активируются с особой силой. В 2026 году ваша натальная карта получает мощную поддержку в сфере карьеры и самовыражения, но требует внимания к эмоциональному здоровью.`,
  activatedThemes: [
    {
      theme: 'Карьерный потенциал (Юпитер натальный в 10 доме)',
      activation:
        'Солнце и Сатурн соляра в 10 доме усиливают натальный Юпитер. Ваш врождённый потенциал к профессиональному росту получает максимальную поддержку.',
    },
    {
      theme: 'Ораторский талант (Солнце + Меркурий в 9 доме)',
      activation:
        'Меркурий соляра в 3 доме активирует коммуникативные навыки. Год для публичных выступлений, написания книг и преподавания.',
    },
    {
      theme: 'Эмоциональная глубина (Луна + Плутон в 12 доме)',
      activation:
        'Луна соляра в 6 доме в Скорпионе резонирует с натальной Луной. Эмоциональная интенсивность усиливается, но направляется в рабочую сферу.',
    },
    {
      theme: 'Домашняя сфера (4 дом натальный в Рыбах)',
      activation:
        'Юпитер соляра в 4 доме расширяет и улучшает домашнюю сферу. Возможен переезд, ремонт или прибавление в семье.',
    },
  ],
  importantEvents: `Наиболее значимые события года будут связаны с пересечением солярных и натальных энергий в 10-м доме (карьера), 4-м доме (дом и семья) и 3-м доме (обучение и коммуникации). Это три главные арены, на которых будет разворачиваться ваш 2026 год.`,
}

export const COMBO_YEAR_LIFE_AREAS = [
  {
    id: 'career',
    title: 'Карьера и финансы',
    icon: 'briefcase',
    color: 'emerald',
    text: 'Ключевая сфера года. Солнце и Сатурн в 10 доме требуют максимум профессионализма. Возможен серьёзный карьерный рост через упорный труд. Финансы стабильны, доходы могут расти через недвижимость или семейную поддержку (Венера во 2 доме в Раке).',
  },
  {
    id: 'relationships',
    title: 'Отношения',
    icon: 'heart',
    color: 'rose',
    text: 'Сфера отношений отходит на второй план по сравнению с карьерой. Венера в Раке делает вас более чувствительным. Важно не переносить рабочие стрессы в дом. Для одиноких — знакомства через работу или поездки.',
  },
  {
    id: 'development',
    title: 'Личное развитие',
    icon: 'trending-up',
    color: 'indigo',
    text: 'Колоссальный интеллектуальный рост (Асцендент в Близнецах, Меркурий в 3 доме). Выберите 1-2 направления обучения, которые помогут карьере. Плутон в 9 доме потребует пересмотра глобальных убеждений.',
  },
  {
    id: 'health',
    title: 'Здоровье',
    icon: 'activity',
    color: 'teal',
    text: 'Марс в 1 доме даёт много энергии, но Луна в 6 доме предупреждает о психосоматике. Важно: режим труда и отдыха, регулярный спорт, дыхательные практики, достаточный сон.',
  },
  {
    id: 'social',
    title: 'Социальная активность',
    icon: 'users',
    color: 'sky',
    text: 'Нептун в 11 доме привлекает творческих и духовных друзей. Круг общения расширится через нетворкинг. Выбирайте окружение осознанно — в этом году связи решают многое.',
  },
]

export const COMBO_RECOMMENDATIONS = [
  {
    title: 'Используйте карьерное окно',
    text: 'Март-май — период максимальных возможностей. Заявляйте о своих амбициях, запускайте проекты, ведите переговоры о повышении. Солнце и Юпитер поддерживают вас.',
  },
  {
    title: 'Управляйте информационным потоком',
    text: 'С Асцендентом в Близнецах вы получите море информации. Фильтруйте её. Ведите ежедневник, структурируйте знания. Не распыляйтесь на множество курсов.',
  },
  {
    title: 'Балансируйте работу и дом',
    text: 'Оппозиция Юпитера и Сатурна потребует виртуозного тайм-менеджмента. Не жертвуйте семьёй ради карьеры. Дом должен стать вашим ресурсом, а не обузой.',
  },
  {
    title: 'Берегите эмоциональное здоровье',
    text: 'Луна в 6 доме в Скорпионе — сигнал о риске выгорания. Найдите способы сброса напряжения: спорт, медитация, прогулки. Не копите обиды.',
  },
  {
    title: 'Развивайте ораторские навыки',
    text: 'Ваш натальный потенциал (Солнце + Меркурий в 9 доме) получает мощную поддержку от Меркурия соляра в 3 доме. Выступайте, пишите, преподавайте.',
  },
  {
    title: 'Инвестируйте в жильё',
    text: 'Юпитер в 4 доме создаёт благоприятные условия для решения жилищных вопросов. Покупка, ремонт, переезд — всё это поддержано энергиями года.',
  },
]

export const COMBO_FINAL_SUMMARY = {
  overview: `Алексей, 2026 год — это год, когда ваши врождённые таланты получают максимальную поддержку от планетарных энергий. Ваша львиная харизма, скорпионья глубина и стрельцовский размах находят идеальное применение в карьерной сфере, которая становится главной ареной года.`,
  mainThemes: [
    'Карьерный прорыв на базе врождённых талантов',
    'Интеллектуальный рост и расширение связей',
    'Баланс между амбициями и семейными ценностями',
    'Эмоциональная зрелость через рабочие вызовы',
  ],
  opportunities: `Главные возможности: повышение социального статуса, улучшение жилищных условий, расширение профессиональных связей и реализация ораторского потенциала. Март-май — окно максимальных возможностей.`,
  lessons: `Ключевые уроки: научиться балансировать между амбициями и потребностями семьи, управлять энергией без выгорания, использовать эмоциональную глубину как ресурс, а не как источник стресса.`,
}
